import React, { useContext, useState, useEffect } from "react";
import { auth, db } from "../../../firebase/config";
import { doc, getDoc, deleteDoc, updateDoc, deleteField, collection, getDocs } from "firebase/firestore";
import { deleteUser } from "firebase/auth";
import { 
  AlertTriangle, 
  Lock, 
  Shield, 
  Key, 
  X,
  Trash2,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  WifiOff,
  Building2
} from "lucide-react";
import styles from "./Profile.module.scss";
import Context from "../../../Context";
import Link from "../../Link";import { apiFetch } from "../../../utils/api";
const Profile: React.FC = () => {
  const { linkSuccess, linkToken, backend, dispatch } = useContext(Context);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [joinDate, setJoinDate] = useState("");
  const [showPlaidOverlay, setShowPlaidOverlay] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deletionProgress, setDeletionProgress] = useState("");
  const [isBankConnected, setIsBankConnected] = useState(false);
  const [connectedAt, setConnectedAt] = useState("");
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectError, setReconnectError] = useState("");

  useEffect(() => {
    const loadUserData = async () => {
      if (auth.currentUser) {
        const email = auth.currentUser.email || "";
        setUserEmail(email);
        setJoinDate(auth.currentUser.metadata.creationTime || "");

        try {
          // Check if bank is connected and get user data
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          
          // Document might not exist for new users
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Load bank connection status
            if (userData?.plaidAccessToken) {
              setIsBankConnected(true);
              if (userData.plaidConnectedAt) {
                setConnectedAt(userData.plaidConnectedAt);
              }
            } else {
              setIsBankConnected(false);
            }
            
            // Get actual name from Firestore, fallback to display name or email
            if (userData?.name) {
              setUserName(userData.name);
            } else if (auth.currentUser.displayName) {
              setUserName(auth.currentUser.displayName);
            } else {
              // Fallback to email username
              setUserName(email.split("@")[0]);
            }
          } else {
            // No Firestore doc (new user) - definitely not connected
            setUserName(auth.currentUser.displayName || email.split("@")[0]);
          }
        } catch (error) {
          console.error("Error loading user data:", error);
          // Default to not connected on error
          // Fallback to email username
          setUserName(email.split("@")[0]);
        }
      }
    };

    loadUserData();
  }, []);

  // Update when link succeeds
  useEffect(() => {
    const updateConnectionStatus = async () => {
      if (linkSuccess) {
        setShowPlaidOverlay(false); // Remove overlay when link succeeds
        
        // Re-check Firestore to ensure we have the latest data
        if (auth.currentUser) {
          try {
            // Small delay to ensure Firestore write has completed
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (userDoc.exists() && userDoc.data()?.plaidAccessToken) {
              console.log("✅ Profile: Bank connection confirmed");
            } else {
              console.warn("⚠️ Profile: linkSuccess is true but token not found");
            }
          } catch (error) {
            console.error("Error updating connection status:", error);
          }
        }
      }
    };

    updateConnectionStatus();
  }, [linkSuccess]);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      setDeleteError("Please type DELETE to confirm");
      return;
    }

    setIsDeleting(true);
    setDeleteError("");
    setDeletionProgress("");

    try {
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error("No user logged in");
      }

      console.log("🗑️ Starting complete account deletion process...");

      // Delete ALL subcollections and nested data
      
      // 1. Delete incomeCache subcollection
      setDeletionProgress("Deleting income cache...");
      const incomeCacheRef = collection(db, "users", user.uid, "incomeCache");
      const incomeCacheDocs = await getDocs(incomeCacheRef);
      for (const docSnapshot of incomeCacheDocs.docs) {
        await deleteDoc(doc(db, "users", user.uid, "incomeCache", docSnapshot.id));
      }
      console.log(`✅ Deleted ${incomeCacheDocs.size} incomeCache documents`);

      // 2. Delete billsCache subcollection
      setDeletionProgress("Deleting bills cache...");
      const billsCacheRef = collection(db, "users", user.uid, "billsCache");
      const billsCacheDocs = await getDocs(billsCacheRef);
      for (const docSnapshot of billsCacheDocs.docs) {
        await deleteDoc(doc(db, "users", user.uid, "billsCache", docSnapshot.id));
      }
      console.log(`✅ Deleted ${billsCacheDocs.size} billsCache documents`);

      // 3. Delete financialData subcollection (income, bills, debts, transactions, investments)
      setDeletionProgress("Deleting financial data (income, bills, debts, transactions, investments)...");
      const financialDataRef = collection(db, "users", user.uid, "financialData");
      const financialDataDocs = await getDocs(financialDataRef);
      for (const docSnapshot of financialDataDocs.docs) {
        await deleteDoc(doc(db, "users", user.uid, "financialData", docSnapshot.id));
      }
      console.log(`✅ Deleted ${financialDataDocs.size} financialData documents`);

      // 4. Delete aiInsights subcollection
      setDeletionProgress("Deleting AI insights and chat history...");
      const aiInsightsRef = collection(db, "users", user.uid, "aiInsights");
      const aiInsightsDocs = await getDocs(aiInsightsRef);
      for (const docSnapshot of aiInsightsDocs.docs) {
        await deleteDoc(doc(db, "users", user.uid, "aiInsights", docSnapshot.id));
      }
      console.log(`✅ Deleted ${aiInsightsDocs.size} aiInsights documents`);

      // 5. Delete any other potential subcollections
      setDeletionProgress("Checking for additional data...");
      const possibleCollections = [
        "transactions", 
        "investments", 
        "settings", 
        "notifications",
        "plaidData",
        "cache"
      ];
      
      for (const collectionName of possibleCollections) {
        try {
          const collectionRef = collection(db, "users", user.uid, collectionName);
          const collectionDocs = await getDocs(collectionRef);
          if (collectionDocs.size > 0) {
            for (const docSnapshot of collectionDocs.docs) {
              await deleteDoc(doc(db, "users", user.uid, collectionName, docSnapshot.id));
            }
            console.log(`✅ Deleted ${collectionDocs.size} ${collectionName} documents`);
          }
        } catch (err) {
          // Collection might not exist, which is fine
          console.log(`ℹ️ Collection ${collectionName} does not exist or is empty`);
        }
      }

      // 6. Delete the main user document
      setDeletionProgress("Deleting user profile...");
      const userDocRef = doc(db, "users", user.uid);
      await deleteDoc(userDocRef);
      console.log("✅ Deleted main user document");

      // 7. Delete the user account from Firebase Auth
      setDeletionProgress("Deleting account...");
      await deleteUser(user);
      console.log("✅ Account and all associated data deleted successfully");
      setDeletionProgress("Account deleted successfully!");

      // User will be automatically logged out and redirected
    } catch (error: any) {
      console.error("❌ Error deleting account:", error);
      
      // Handle re-authentication required error
      if (error.code === "auth/requires-recent-login") {
        setDeleteError("For security, please log out and log back in before deleting your account.");
      } else {
        setDeleteError(`Failed to delete account: ${error.message}. Please try again or contact support.`);
      }
      
      setIsDeleting(false);
      setDeletionProgress("");
    }
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    setReconnectError("");
    try {
      // 1. Remove Plaid item and clear backend memory
      await apiFetch('/api/bank/disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      // 2. Clear Plaid fields from Firestore
      if (auth.currentUser) {
        await updateDoc(doc(db, "users", auth.currentUser.uid), {
          plaidAccessToken: deleteField(),
          plaidItemId: deleteField(),
          plaidConnectedAt: deleteField(),
        });
      }

      // 3. Reset context so the rest of the app knows bank is disconnected
      dispatch({
        type: "SET_STATE",
        state: { linkSuccess: false, accessToken: null, itemId: null },
      });
      setIsBankConnected(false);
      setConnectedAt("");

      // 4. Open Plaid Link to connect again
      setShowPlaidOverlay(true);
    } catch (error: any) {
      console.error("Error reconnecting bank:", error);
      setReconnectError("Failed to disconnect. Please try again.");
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleDeleteModalOpen = () => {
    setShowDeleteModal(true);
    setDeleteConfirmText("");
    setDeleteError("");
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText("");
    setDeleteError("");
  };

  return (
    <div className={styles.profile}>
      <div className={styles.content}>
        {/* Profile Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Account Information</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Name</label>
                <p>{userName}</p>
              </div>
              <div className={styles.infoItem}>
                <label>Email</label>
                <p>{userEmail}</p>
              </div>
              <div className={styles.infoItem}>
                <label>Member Since</label>
                <p>{new Date(joinDate).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Connection Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Bank Connection</h2>
            {isBankConnected ? (
              <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                <CheckCircle2 size={12} /> Connected
              </span>
            ) : (
              <span className={`${styles.badge} ${styles.badgeWarning}`}>
                <WifiOff size={12} /> Not Connected
              </span>
            )}
          </div>
          <div className={styles.cardBody}>
            {isBankConnected ? (
              <div className={styles.bankConnectedSection}>
                <div className={styles.bankInfo}>
                  <span className={styles.bankIcon}><Building2 size={28} /></span>
                  <div>
                    <p className={styles.bankStatus}>Your bank account is connected via Plaid.</p>
                    {connectedAt && (
                      <p className={styles.bankDate}>
                        Connected on {new Date(connectedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                {reconnectError && (
                  <p className={styles.reconnectError}>
                    <AlertTriangle size={14} /> {reconnectError}
                  </p>
                )}
                <button
                  className={styles.reconnectBtn}
                  onClick={handleReconnect}
                  disabled={isReconnecting}
                >
                  <RefreshCw size={16} />
                  {isReconnecting ? "Disconnecting…" : "Reconnect Bank"}
                </button>
              </div>
            ) : (
              <div className={styles.bankConnectedSection}>
                <div className={styles.bankInfo}>
                  <span className={styles.bankIcon}><WifiOff size={28} /></span>
                  <div>
                    <p className={styles.bankStatus}>No bank account connected.</p>
                    <p className={styles.bankDate}>Connect your bank to see your financial overview.</p>
                  </div>
                </div>
                <button
                  className={styles.connectBtn}
                  onClick={() => setShowPlaidOverlay(true)}
                  disabled={!backend || !linkToken}
                >
                  Connect Bank Account
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Security Card */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Security & Privacy</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.securityFeatures}>
              <div className={styles.securityItem}>
                <span className={styles.securityIcon}><Lock size={24} /></span>
                <div>
                  <h4>256-bit Encryption</h4>
                  <p>Bank-level security for all your data</p>
                </div>
              </div>
              <div className={styles.securityItem}>
                <span className={styles.securityIcon}><Shield size={24} /></span>
                <div>
                  <h4>Secure Authentication</h4>
                  <p>Firebase-powered login protection</p>
                </div>
              </div>
              <div className={styles.securityItem}>
                <span className={styles.securityIcon}><Key size={24} /></span>
                <div>
                  <h4>Plaid Integration</h4>
                  <p>Your credentials are never stored</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone - Delete Account */}
        <div className={`${styles.card} ${styles.dangerCard}`}>
          <div className={styles.cardHeader}>
            <h2>Danger Zone</h2>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.dangerZone}>
              <div className={styles.dangerInfo}>
                <h3>Delete Account</h3>
                <p>
                  Permanently delete your ZEBED account and all associated data. 
                  This action cannot be undone.
                </p>
              </div>
              <button 
                className={styles.dangerBtn}
                onClick={handleDeleteModalOpen}
              >
                <Trash2 size={18} />
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Plaid Link Overlay - Blurred background while Plaid UI is open */}
      {showPlaidOverlay && (
        <div className={styles.plaidOverlay}>
          <div className={styles.plaidLinkWrapper}>
            {!backend ? (
              <div className={styles.plaidError}>
                <AlertTriangle size={48} />
                <h3>Backend server is not running</h3>
                <button 
                  className={styles.secondaryBtn}
                  onClick={() => setShowPlaidOverlay(false)}
                >
                  Close
                </button>
              </div>
            ) : linkToken === "" ? (
              <div className={styles.plaidLoading}>
                <div className={styles.spinner}></div>
                <p>Preparing secure connection...</p>
              </div>
            ) : (
              <Link />
            )}
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay} onClick={handleDeleteModalClose}>
          <div className={`${styles.modalContent} ${styles.deleteModalContent}`} onClick={(e) => e.stopPropagation()}>
            <button 
              className={styles.modalClose}
              onClick={handleDeleteModalClose}
            >
              <X size={24} />
            </button>

            <div className={styles.deleteModalHeader}>
              <span className={styles.deleteModalIcon}>
                <AlertCircle size={56} />
              </span>
              <h2>Delete Account?</h2>
            </div>

            <div className={styles.deleteWarningBox}>
              <h3>⚠️ Warning: This action is permanent!</h3>
              <p>Deleting your account will permanently remove:</p>
              <ul className={styles.deleteWarningList}>
                <li><strong>All financial data:</strong> income, bills, debts, spending transactions, investments</li>
                <li><strong>AI Insights:</strong> all generated insights and chat history</li>
                <li><strong>Bank connections:</strong> all linked Plaid accounts and access tokens</li>
                <li><strong>Cached data:</strong> income cache, bills cache, and all stored calculations</li>
                <li><strong>Profile information:</strong> your name, email, and account settings</li>
                <li><strong>Account authentication:</strong> you will be logged out and cannot recover this account</li>
                <li className={styles.warningHighlight}>⚠️ <strong>This action CANNOT be undone or recovered</strong></li>
              </ul>
            </div>

            <div className={styles.deleteConfirmSection}>
              <p className={styles.deleteConfirmText}>
                To confirm deletion, please type <strong>DELETE</strong> below:
              </p>
              <input
                type="text"
                className={styles.deleteConfirmInput}
                placeholder="Type DELETE to confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                disabled={isDeleting}
              />
            </div>

            {deleteError && (
              <div className={styles.deleteError}>
                <AlertTriangle size={18} />
                {deleteError}
              </div>
            )}

            {deletionProgress && (
              <div className={styles.deletionProgress}>
                <div className={styles.progressSpinner}></div>
                {deletionProgress}
              </div>
            )}

            <div className={styles.deleteModalActions}>
              <button 
                className={styles.deleteConfirmBtn}
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== "DELETE" || isDeleting}
              >
                {isDeleting ? (
                  "Deleting..."
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete My Account
                  </>
                )}
              </button>
              <button 
                className={styles.deleteCancelBtn}
                onClick={handleDeleteModalClose}
                disabled={isDeleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Profile;
